import * as React from "react";

import { BrandStoryPanel } from "@/components/auth/BrandStoryPanel";
import { cn } from "@/lib/cn";

/**
 * AuthShell — the ONE pre-auth shell (canvas 01b `auth/*`, MV2-907).
 *
 * Replaces the three hand-rolled copies Login, Forgot and Accept-invite each
 * carried. Pre-auth is platform-branded only (CROSS_PAGE_DECISIONS §20): the
 * hero half is `BrandStoryPanel` (fleet map, headline, legal links); the
 * form half is a single 384 px column on `--gray-0`. Below `lg` the hero
 * collapses to the compact brand band and the form takes the width, with
 * the board's footer row (legal · "re:bot Monitoring") underneath.
 *
 * `headline` / `sub` let each flow set the hero copy the boards draw
 * ("Back in two steps.", "Welcome aboard.", …).
 */
export interface AuthShellProps {
  headline?: string;
  sub?: string;
  children: React.ReactNode;
  className?: string;
}

export function AuthShell({
  headline,
  sub,
  children,
  className,
}: AuthShellProps): React.ReactElement {
  return (
    <div
      className={cn(
        "flex min-h-screen flex-col bg-[color:var(--gray-0)] lg:flex-row",
        className,
      )}
    >
      <BrandStoryPanel headline={headline} sub={sub} />
      <BrandStoryPanel compact />

      <main className="flex flex-1 flex-col lg:items-center lg:justify-center lg:px-8 lg:py-10">
        {/* Board: desktop column is exactly 384 wide, centred; mobile the
            form fills the width at 26/20 padding. */}
        <div className="flex w-full flex-1 flex-col px-5 py-[26px] lg:w-[384px] lg:flex-none lg:p-0">
          {children}
        </div>
        <footer className="flex items-center border-t border-gray-200 px-5 py-3.5 lg:hidden">
          <span className="text-[11px] leading-[1.4] text-gray-500">
            <a
              href="https://renewablebot.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-900"
            >
              Privacy policy
            </a>
            {" · "}
            <a
              href="https://renewablebot.com/terms-of-services-commercial/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-900"
            >
              Terms
            </a>
          </span>
          <span className="flex-1" />
          <span className="type-eyebrow text-gray-500">re:bot Monitoring</span>
        </footer>
      </main>
    </div>
  );
}
